/**
 * Deploy-Mesh Setup panel (admin).
 *
 * Self-serve UI wrapping the proven tunneled-deploy flow (server routes in
 * packages/server/src/routes/api/integrations/mesh-setup.ts):
 *   1. Show mesh pairing presence (non-secret coordinates only).
 *   2. Register this Maestro as a remote agent → reveal minted secrets ONCE.
 *   3. List + create deployment targets (tunneled or direct).
 *
 * Hygiene:
 *   - The pairing status NEVER shows forwardToken/operatorSigningKey.
 *   - The register response reveals those secrets exactly once (eye-toggle,
 *     copy, "shown once" warning); they are otherwise only persisted server-side.
 *   - The mesh admin token lives in server env, never entered here.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  XCircle,
  Link2,
  Eye,
  EyeOff,
  Copy,
  Check,
  ServerCog,
  Plus,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  getAuthSession,
  getMeshConfig,
  registerMeshRemoteAgent,
  listDeployTargets,
  createDeployTarget,
  type RegisterMeshAgentResponse,
  type DeployTarget,
} from '@/lib/api';

const MESH_CONFIG_KEY = ['meshConfig'] as const;
const DEPLOY_TARGETS_KEY = ['deployTargets'] as const;

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  testId?: string;
}): React.ReactElement {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-text-secondary">{props.label}</span>
      <Input
        value={props.value}
        onChange={e => {
          props.onChange(e.target.value);
        }}
        placeholder={props.placeholder}
        disabled={props.disabled}
        spellCheck={false}
        className="font-mono text-sm"
        data-testid={props.testId}
      />
    </label>
  );
}

function RevealRow(props: { label: string; value: string }): React.ReactElement {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    void navigator.clipboard?.writeText(props.value);
    setCopied(true);
    globalThis.setTimeout(() => {
      setCopied(false);
    }, 1500);
  };
  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-text-secondary">{props.label}</span>
      <div className="flex items-center gap-2">
        <Input
          type={show ? 'text' : 'password'}
          value={props.value}
          readOnly
          spellCheck={false}
          autoComplete="off"
          className="flex-1 font-mono text-sm"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9"
          aria-label={show ? 'Hide secret' : 'Show secret'}
          onClick={() => {
            setShow(c => !c);
          }}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9"
          aria-label="Copy secret"
          onClick={copy}
        >
          {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export function DeployMeshSetupPanel(): React.ReactElement | null {
  const queryClient = useQueryClient();

  const { data: session } = useQuery({ queryKey: ['authSession'], queryFn: getAuthSession });
  const isAdmin = session?.operator?.role === 'admin';

  const configQuery = useQuery({
    queryKey: MESH_CONFIG_KEY,
    queryFn: getMeshConfig,
    enabled: isAdmin,
    staleTime: 30_000,
  });
  const targetsQuery = useQuery({
    queryKey: DEPLOY_TARGETS_KEY,
    queryFn: listDeployTargets,
    enabled: isAdmin,
    staleTime: 30_000,
  });

  // Register-agent form
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [meshBaseUrl, setMeshBaseUrl] = useState('');
  const [revealed, setRevealed] = useState<RegisterMeshAgentResponse | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);

  const registerMutation = useMutation({
    mutationFn: registerMeshRemoteAgent,
    onMutate: () => {
      setRegisterError(null);
      setRevealed(null);
    },
    onSuccess: result => {
      setRevealed(result);
      void queryClient.invalidateQueries({ queryKey: MESH_CONFIG_KEY });
    },
    onError: error => {
      setRegisterError(toErrorMessage(error));
    },
  });

  // Add-target form (tunneled compose)
  const [tOutcomeId, setTOutcomeId] = useState('');
  const [tTargetName, setTTargetName] = useState('');
  const [tEnvironment, setTEnvironment] = useState('staging');
  const [tMeshSlug, setTMeshSlug] = useState('');
  const [tComposeFile, setTComposeFile] = useState('');
  const [tProjectName, setTProjectName] = useState('');
  const [tBaseUrl, setTBaseUrl] = useState('');
  const [tHealthUrl, setTHealthUrl] = useState('/');
  const [targetError, setTargetError] = useState<string | null>(null);

  const targetMutation = useMutation({
    mutationFn: createDeployTarget,
    onMutate: () => {
      setTargetError(null);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DEPLOY_TARGETS_KEY });
      setTOutcomeId('');
      setTTargetName('');
      setTMeshSlug('');
      setTComposeFile('');
      setTProjectName('');
      setTBaseUrl('');
    },
    onError: error => {
      setTargetError(toErrorMessage(error));
    },
  });

  if (session && !isAdmin) {
    return (
      <Card>
        <CardContent className="py-4 text-sm text-text-secondary">
          Deploy-mesh setup requires an admin account.
        </CardContent>
      </Card>
    );
  }

  const cfg = configQuery.data;
  const paired = cfg?.paired === true;
  const canRegister = slug.trim().length > 0 && displayName.trim().length > 0;
  const canAddTarget =
    tOutcomeId.trim() !== '' &&
    tTargetName.trim() !== '' &&
    tEnvironment.trim() !== '' &&
    tMeshSlug.trim() !== '' &&
    tComposeFile.trim() !== '' &&
    tProjectName.trim() !== '';

  const submitTarget = (): void => {
    targetMutation.mutate({
      outcomeId: tOutcomeId.trim(),
      targetName: tTargetName.trim(),
      environment: tEnvironment.trim(),
      targetType: 'compose',
      executionMode: 'tunneled',
      executionConfig: {
        meshSlug: tMeshSlug.trim(),
        composeFile: tComposeFile.trim(),
        projectName: tProjectName.trim(),
        ...(tBaseUrl.trim() !== '' ? { baseUrl: tBaseUrl.trim() } : {}),
      },
      healthCheck: { url: tHealthUrl.trim() || '/', timeoutSeconds: 10 },
    });
  };

  return (
    <div className="space-y-4">
      {/* 1. Pairing status */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-4 w-4 text-primary" /> Mesh Pairing
            </CardTitle>
            {configQuery.isLoading ? (
              <Badge variant="outline" className="text-xs">
                Loading…
              </Badge>
            ) : paired ? (
              <Badge className="bg-success text-white text-xs hover:bg-success">Paired</Badge>
            ) : (
              <Badge variant="secondary" className="text-xs">
                Not paired
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {paired ? (
            <div className="grid grid-cols-1 gap-1 rounded-lg bg-surface-elevated/50 p-3">
              <PresenceRow label="Agent slug" value={cfg?.agentSlug} />
              <PresenceRow label="Product" value={cfg?.product} />
              <PresenceRow label="Tunnel URL" value={cfg?.tunnelUrl} />
              <PresenceRow label="Paired at" value={cfg?.pairedAt} />
            </div>
          ) : (
            <div className="text-text-secondary">
              This Maestro is not yet paired to a mesh. Register a remote agent below to establish a
              tunnel.
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2. Register remote agent */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <ServerCog className="h-4 w-4 text-primary" /> Register Remote Agent
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-text-secondary">
            Registers this Maestro on the AgentMesh and persists the tunnel coordinates. The mesh
            admin token is read from the server environment — never entered here.
          </div>
          <Field
            label="Agent slug"
            value={slug}
            onChange={setSlug}
            placeholder="rb-canary"
            disabled={registerMutation.isPending}
            testId="mesh-agent-slug"
          />
          <Field
            label="Display name"
            value={displayName}
            onChange={setDisplayName}
            placeholder="Rollback Canary"
            disabled={registerMutation.isPending}
            testId="mesh-agent-display-name"
          />
          <Field
            label="Mesh base URL (optional — defaults to server DEPLOY_MESH_BASE_URL)"
            value={meshBaseUrl}
            onChange={setMeshBaseUrl}
            placeholder="https://apps.mahoosuc.ai"
            disabled={registerMutation.isPending}
          />
          <Button
            onClick={() => {
              registerMutation.mutate({
                slug: slug.trim(),
                displayName: displayName.trim(),
                ...(meshBaseUrl.trim() !== '' ? { meshBaseUrl: meshBaseUrl.trim() } : {}),
              });
            }}
            disabled={!canRegister || registerMutation.isPending}
            className="gap-2"
            data-testid="mesh-register-submit"
          >
            <ServerCog className="h-4 w-4" />
            {registerMutation.isPending ? 'Registering…' : 'Register agent'}
          </Button>

          {registerError && <p className="text-sm text-error">{registerError}</p>}

          {revealed && (
            <div className="space-y-3 rounded-lg border border-warning/40 bg-warning/5 p-3">
              <p className="text-[11px] font-medium text-warning">
                These secrets are shown once and will not appear again. They are already saved to{' '}
                <span className="font-mono">{revealed.meshConfigPath}</span> — copy them now only if
                you need them elsewhere.
              </p>
              <RevealRow label="forwardToken" value={revealed.forwardToken} />
              {revealed.operatorSigningKey && (
                <RevealRow label="operatorSigningKey" value={revealed.operatorSigningKey} />
              )}
              {revealed.webhookSecret && (
                <RevealRow label="webhookSecret" value={revealed.webhookSecret} />
              )}
              <div className="text-xs text-text-secondary">
                Operator URL: <span className="font-mono">{revealed.operatorUrl}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3. Deploy targets */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <ServerCog className="h-4 w-4 text-primary" /> Deployment Targets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {targetsQuery.data && targetsQuery.data.targets.length > 0 ? (
            <div className="space-y-2">
              {targetsQuery.data.targets.map((t: DeployTarget) => (
                <div
                  key={t.id}
                  className="flex items-center gap-2 rounded-md bg-surface-elevated/50 p-2 text-sm"
                >
                  <div className="flex-1">
                    <div className="font-mono text-xs text-text-secondary">{t.targetName}</div>
                    <div className="text-xs text-text-tertiary mt-0.5">
                      {t.outcomeId} → {t.environment}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {t.executionMode}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {t.targetType}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-text-secondary">No deployment targets yet.</div>
          )}

          <div className="space-y-2 rounded-lg border border-border p-3">
            <div className="text-xs font-medium text-text-secondary">
              Add a tunneled compose target
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Field
                label="Outcome ID"
                value={tOutcomeId}
                onChange={setTOutcomeId}
                placeholder="O-2026-08-17-my-app"
                testId="target-outcome-id"
              />
              <Field
                label="Target name"
                value={tTargetName}
                onChange={setTTargetName}
                placeholder="my-app"
              />
              <Field
                label="Environment"
                value={tEnvironment}
                onChange={setTEnvironment}
                placeholder="staging"
              />
              <Field
                label="Mesh slug"
                value={tMeshSlug}
                onChange={setTMeshSlug}
                placeholder="rb-canary"
              />
              <Field
                label="Compose file (absolute)"
                value={tComposeFile}
                onChange={setTComposeFile}
                placeholder="/opt/app/docker-compose.yml"
              />
              <Field
                label="Project name"
                value={tProjectName}
                onChange={setTProjectName}
                placeholder="my-app"
              />
              <Field
                label="Base URL (health)"
                value={tBaseUrl}
                onChange={setTBaseUrl}
                placeholder="http://127.0.0.1:8080"
              />
              <Field
                label="Health path"
                value={tHealthUrl}
                onChange={setTHealthUrl}
                placeholder="/"
              />
            </div>
            <Button
              onClick={submitTarget}
              disabled={!canAddTarget || targetMutation.isPending}
              className="gap-2"
              data-testid="target-submit"
            >
              <Plus className="h-4 w-4" />
              {targetMutation.isPending ? 'Adding…' : 'Add target'}
            </Button>
            {targetError && <p className="text-sm text-error">{targetError}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PresenceRow(props: { label: string; value?: string }): React.ReactElement {
  return (
    <div className="flex items-center gap-2 text-sm">
      {props.value ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
      ) : (
        <XCircle className="h-4 w-4 shrink-0 text-warning" />
      )}
      <span className="text-text-secondary">{props.label}</span>
      <span className="ml-auto font-mono text-xs text-text-tertiary">{props.value ?? '—'}</span>
    </div>
  );
}
